import { t } from '@lingui/core/macro';

import {
  analyseRequirementSet,
  type RequirementSetMode,
} from '@duke-hq/unit-generation-pipeline/requirements/requirementSetRules';

import type { RouterInputs } from '@duke-hq/app-admin-web/lib/trpcAdmin';

export type RequirementSet = NonNullable<
  RouterInputs['unitsOfCompetency']['triggerUnitGeneration']['reviewedRequirements']
>;

export type EvidenceItem = RequirementSet['knowledgeEvidence'][number];

export type SourceUnit =
  RouterInputs['unitsOfCompetency']['triggerUnitGeneration']['publishedUnit'];

export function requirementsToReview(source: SourceUnit): RequirementSet {
  const {
    elements,
    performanceCriteria,
    knowledgeEvidence,
    performanceEvidence,
    foundationSkillsStructured,
    assessmentConditions,
  } = source;

  return {
    elements,
    performanceCriteria,
    knowledgeEvidence,
    performanceEvidence,
    foundationSkillsStructured,
    assessmentConditions,
  };
}

export const EMPTY_REQUIREMENT_SET: RequirementSet = {
  elements: [],
  performanceCriteria: [],
  knowledgeEvidence: [],
  performanceEvidence: [],
  foundationSkillsStructured: [],
  assessmentConditions: '',
};

export type RequirementSetIssues = {
  duplicateCodes: Set<string>;
  duplicateCategories: Set<string>;
  blocking: string[];
};

export function blank(value: string): boolean {
  return value.trim() === '';
}

export function requirementSetIssues(
  set: RequirementSet,
  mode?: RequirementSetMode,
): RequirementSetIssues {
  const analysis = analyseRequirementSet(set, mode);

  const duplicateCodes = new Set(analysis.duplicateCodes.flatMap(({ codes }) => codes));
  const duplicateCategories = new Set(analysis.duplicateCategories);

  const blankCodes = [
    ...set.elements.filter(({ title }) => blank(title)).map(({ code }) => code),
    ...set.performanceCriteria
      .filter(({ description }) => blank(description))
      .map(({ code }) => code),
    ...set.knowledgeEvidence
      .filter(({ description }) => blank(description))
      .map(({ code }) => code),
    ...set.performanceEvidence
      .filter(({ description }) => blank(description))
      .map(({ code }) => code),
  ];

  const blankSkills = set.foundationSkillsStructured.filter(
    ({ category, description }) => blank(category) || blank(description),
  ).length;

  const blocking: string[] = [];

  if (blankCodes.length > 0) {
    blocking.push(t`Fill in the empty rows: ${blankCodes.join(', ')}.`);
  }

  if (blankSkills > 0) {
    blocking.push(t`Every foundation skill needs a category and a description.`);
  }

  if (analysis.needsPerformanceCriterion) {
    blocking.push(t`Add at least one performance criterion.`);
  }

  if (analysis.needsEvidence) {
    blocking.push(t`Add at least one knowledge evidence or performance evidence item.`);
  }

  if (analysis.unexpectedPerformanceEvidence) {
    blocking.push(t`An auto-assessed unit has no performance evidence.`);
  }

  if (duplicateCodes.size > 0) {
    blocking.push(t`Two requirements share a code: ${[...duplicateCodes].join(', ')}.`);
  }

  if (duplicateCategories.size > 0) {
    blocking.push(
      t`Two foundation skills share a category: ${[...duplicateCategories].join(', ')}.`,
    );
  }

  if (analysis.orphanedCriteria.length > 0) {
    blocking.push(t`These criteria belong to no element: ${analysis.orphanedCriteria.join(', ')}.`);
  }

  return { duplicateCodes, duplicateCategories, blocking };
}

export function nonAccreditedSourceUnit({
  unitCode,
  unitTitle,
  description,
}: {
  unitCode: string;
  unitTitle: string;
  description: string;
}): SourceUnit {
  return {
    ...EMPTY_REQUIREMENT_SET,
    unitCode,
    unitTitle,
    FullPage: description,
    application: description,
    requirementCounts: { pc: 0, ke: 0, pe: 0 },
  };
}

export function cleanUnitCode(value: string): string {
  return value.replace(/\s/g, '').toUpperCase();
}
